import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  loadImage,
  renderCrop,
  PHOTO_ASPECT_W,
  PHOTO_ASPECT_H,
  type CropTransform,
} from '../lib/photo';
import './PhotoCropper.scss';

interface Props {
  src: string;
  onConfirm: (dataUrl: string) => void;
  onBack: () => void;
}

const ZOOM_STEP = 0.1;
const MAX_ZOOM_MULT = 4;

export default function PhotoCropper({ src, onConfirm, onBack }: Props) {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [frame, setFrame] = useState({ w: 0, h: 0 });
  // userMult is the user-applied zoom *above* the cover-fit minimum. 1 = exactly cover.
  const [userMult, setUserMult] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  // Reset zoom and pan whenever a new image is supplied.
  const [prevSrc, setPrevSrc] = useState(src);
  if (prevSrc !== src) {
    setPrevSrc(src);
    setUserMult(1);
    setOffset({ x: 0, y: 0 });
  }

  // Pointer-state refs (don't need to re-render on every frame).
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const lastPinchDist = useRef<number | null>(null);
  const dragStart = useRef<{ x: number; y: number; offX: number; offY: number } | null>(null);

  // Load image on src change
  useEffect(() => {
    let alive = true;
    loadImage(src).then((image) => {
      if (alive) setImg(image);
    });
    return () => {
      alive = false;
    };
  }, [src]);

  // Measure frame
  useEffect(() => {
    if (!frameRef.current) return undefined;
    const el = frameRef.current;
    const sync = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setFrame((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    sync();
    return () => ro.disconnect();
  }, []);

  const minScale = useMemo(() => {
    if (!img || !frame.w || !frame.h) return 1;
    return Math.max(frame.w / img.naturalWidth, frame.h / img.naturalHeight);
  }, [img, frame]);
  const scale = minScale * userMult;

  const clampOffset = useCallback(
    (x: number, y: number, s: number) => {
      if (!img) return { x, y };
      const displayW = img.naturalWidth * s;
      const displayH = img.naturalHeight * s;
      const maxX = Math.max(0, (displayW - frame.w) / 2);
      const maxY = Math.max(0, (displayH - frame.h) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, x)),
        y: Math.min(maxY, Math.max(-maxY, y)),
      };
    },
    [img, frame],
  );

  const setMultClamped = useCallback(
    (next: number) => {
      const clamped = Math.min(MAX_ZOOM_MULT, Math.max(1, next));
      setUserMult((prev) => (prev === clamped ? prev : clamped));
      setOffset((prev) => {
        const re = clampOffset(prev.x, prev.y, minScale * clamped);
        return prev.x === re.x && prev.y === re.y ? prev : re;
      });
    },
    [minScale, clampOffset],
  );

  function onPointerDown(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) {
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        offX: offset.x,
        offY: offset.y,
      };
    } else if (pointers.current.size === 2) {
      const pts = Array.from(pointers.current.values());
      lastPinchDist.current = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      dragStart.current = null;
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && lastPinchDist.current !== null) {
      const pts = Array.from(pointers.current.values());
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const ratio = dist / lastPinchDist.current;
      lastPinchDist.current = dist;
      setMultClamped(userMult * ratio);
      return;
    }

    if (pointers.current.size === 1 && dragStart.current) {
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      const next = clampOffset(dragStart.current.offX + dx, dragStart.current.offY + dy, scale);
      setOffset((prev) => (prev.x === next.x && prev.y === next.y ? prev : next));
    }
  }

  function endPointer(e: React.PointerEvent) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) lastPinchDist.current = null;
    if (pointers.current.size === 0) dragStart.current = null;
  }

  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    const delta = -e.deltaY / 500; // smooth, mouse + trackpad
    setMultClamped(userMult * (1 + delta));
  }

  function handleConfirm() {
    if (!img) return;
    const transform: CropTransform = {
      imgW: img.naturalWidth,
      imgH: img.naturalHeight,
      scale,
      offsetX: offset.x,
      offsetY: offset.y,
      frameW: frame.w,
      frameH: frame.h,
      minScale,
    };
    onConfirm(renderCrop(img, transform));
  }

  return (
    <div className="cropper">
      <div
        ref={frameRef}
        className="cropper__frame"
        style={{ aspectRatio: `${PHOTO_ASPECT_W} / ${PHOTO_ASPECT_H}` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onWheel={onWheel}
      >
        {img && (
          <img
            src={src}
            alt=""
            className="cropper__image"
            draggable={false}
            style={{
              width: img.naturalWidth * scale,
              height: img.naturalHeight * scale,
              transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
            }}
          />
        )}
        {/* Stop pointerdown so tapping the pill doesn't seed a pan-drag on the frame. */}
        <div className="cropper__zoom" onPointerDown={(e) => e.stopPropagation()}>
          <button
            type="button"
            aria-label={t('photo.zoomOut')}
            onClick={() => setMultClamped(userMult - ZOOM_STEP)}
            className="cropper__zoom-btn"
            disabled={userMult <= 1}
          >
            <ZoomOutIcon />
          </button>
          <span className="cropper__zoom-divider" aria-hidden="true" />
          <button
            type="button"
            aria-label={t('photo.zoomIn')}
            onClick={() => setMultClamped(userMult + ZOOM_STEP)}
            className="cropper__zoom-btn"
            disabled={userMult >= MAX_ZOOM_MULT}
          >
            <ZoomInIcon />
          </button>
        </div>
      </div>

      <div className="cropper__actions">
        <button type="button" className="cropper__primary" onClick={handleConfirm}>
          {t('common.continue')}
        </button>
        <button type="button" className="cropper__secondary" onClick={onBack}>
          ← {t('photo.uploadDifferent')}
        </button>
      </div>
    </div>
  );
}

function ZoomOutIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M6 12h12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function ZoomInIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M12 6v12M6 12h12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
